import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ReportGenerationWorkspaceTab = 'templates' | 'filters' | 'history';

export type ReportGenerationWorkspaceState = {
  isOpen: boolean;
  collapsed: boolean;
  activeTab: ReportGenerationWorkspaceTab;
  templateView: 'gallery' | 'my';
  versionHistoryTemplateId: string | null;
  selectedHistoryEntryId: string | null;
  reportTitle: string;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc' | null;
  hiddenColumns: string[];
  contextMenuFieldId: string | null;
  showExport: boolean;
  dismissedInsightIds: string[];
};

const initialState: ReportGenerationWorkspaceState = {
  isOpen: false,
  collapsed: false,
  activeTab: 'templates',
  templateView: 'gallery',
  versionHistoryTemplateId: null,
  selectedHistoryEntryId: null,
  reportTitle: 'Tuition report',
  sortColumn: null,
  sortDirection: null,
  hiddenColumns: [],
  contextMenuFieldId: null,
  showExport: false,
  dismissedInsightIds: [],
};

export const reportGenerationWorkspaceSlice = createSlice({
  name: 'reportGenerationWorkspace',
  initialState,
  reducers: {
    openReportGenerationWorkspace: (state, action: PayloadAction<{
      activeTab?: ReportGenerationWorkspaceTab;
      reportTitle?: string;
    } | undefined>) => {
      state.isOpen = true;
      if (action.payload?.activeTab) {
        state.activeTab = action.payload.activeTab;
      }
      if (action.payload?.reportTitle) {
        state.reportTitle = action.payload.reportTitle;
      }
    },
    closeReportGenerationWorkspace: (state) => {
      state.isOpen = false;
      state.showExport = false;
      state.contextMenuFieldId = null;
    },
    setReportGenerationWorkspaceCollapsed: (state, action: PayloadAction<boolean>) => {
      state.collapsed = action.payload;
    },
    setReportGenerationWorkspaceTab: (state, action: PayloadAction<ReportGenerationWorkspaceTab>) => {
      state.activeTab = action.payload;
      if (action.payload !== 'templates') {
        state.versionHistoryTemplateId = null;
      }
      if (action.payload !== 'history') {
        state.selectedHistoryEntryId = null;
      }
    },
    setReportGenerationTemplateView: (state, action: PayloadAction<'gallery' | 'my'>) => {
      state.templateView = action.payload;
      state.versionHistoryTemplateId = null;
    },
    openReportGenerationTemplateVersionHistory: (state, action: PayloadAction<string>) => {
      state.activeTab = 'templates';
      state.templateView = 'my';
      state.versionHistoryTemplateId = action.payload;
    },
    closeReportGenerationTemplateVersionHistory: (state) => {
      state.versionHistoryTemplateId = null;
    },
    setReportGenerationSelectedHistoryEntry: (state, action: PayloadAction<string | null>) => {
      state.selectedHistoryEntryId = action.payload;
    },
    setReportGenerationWorkspaceTitle: (state, action: PayloadAction<string>) => {
      state.reportTitle = action.payload;
    },
    toggleReportGenerationWorkspaceSort: (state, action: PayloadAction<string>) => {
      const fieldId = action.payload;
      if (state.sortColumn !== fieldId) {
        state.sortColumn = fieldId;
        state.sortDirection = 'asc';
        return;
      }
      if (state.sortDirection === 'asc') {
        state.sortDirection = 'desc';
        return;
      }
      state.sortColumn = null;
      state.sortDirection = null;
    },
    toggleReportGenerationHiddenColumn: (state, action: PayloadAction<string>) => {
      const fieldId = action.payload;
      state.hiddenColumns = state.hiddenColumns.includes(fieldId)
        ? state.hiddenColumns.filter((value) => value !== fieldId)
        : [...state.hiddenColumns, fieldId];
    },
    setReportGenerationContextMenuField: (state, action: PayloadAction<string | null>) => {
      state.contextMenuFieldId = action.payload;
    },
    openReportGenerationExport: (state) => {
      state.showExport = true;
    },
    closeReportGenerationExport: (state) => {
      state.showExport = false;
    },
    dismissReportGenerationInsight: (state, action: PayloadAction<string>) => {
      if (!state.dismissedInsightIds.includes(action.payload)) {
        state.dismissedInsightIds = [...state.dismissedInsightIds, action.payload];
      }
    },
    resetReportGenerationWorkspaceView: (state) => {
      state.collapsed = false;
      state.activeTab = 'templates';
      state.templateView = 'gallery';
      state.versionHistoryTemplateId = null;
      state.selectedHistoryEntryId = null;
      state.sortColumn = null;
      state.sortDirection = null;
      state.hiddenColumns = [];
      state.contextMenuFieldId = null;
      state.showExport = false;
      state.dismissedInsightIds = [];
    },
  },
});

export const {
  openReportGenerationWorkspace,
  closeReportGenerationWorkspace,
  setReportGenerationWorkspaceCollapsed,
  setReportGenerationWorkspaceTab,
  setReportGenerationTemplateView,
  openReportGenerationTemplateVersionHistory,
  closeReportGenerationTemplateVersionHistory,
  setReportGenerationSelectedHistoryEntry,
  setReportGenerationWorkspaceTitle,
  toggleReportGenerationWorkspaceSort,
  toggleReportGenerationHiddenColumn,
  setReportGenerationContextMenuField,
  openReportGenerationExport,
  closeReportGenerationExport,
  dismissReportGenerationInsight,
  resetReportGenerationWorkspaceView,
} = reportGenerationWorkspaceSlice.actions;
